import React, { useState, useContext, useEffect } from "react";
import { STATUS_MESSAGE_COLORS, BORDERS, UI_COPY } from "../../../config";
import { useUiText } from "../../../hooks/useUiText";

import { CoreContext } from "../../../context/global/CoreContext";
import { TimekeepingContext } from "../../../context/domain/timekeeping/TimekeepingContext";

import { clockOutUser } from "../../../services/api/timekeeping";

import {
  getCurrentTimeInWindow,
  addHoursTo24TimeString,
} from "../../../utils/datetime";

// Import icons
import { Clock } from "../../../components/icons/svg/outline/index";
import { Button } from "../../../components/ui";
import { ModalWrapper } from "../../../components/ui";

/**
 * ClockOut component allows users to record their clock-out time.
 *
 * The component provides a time input defaulted to either the current time or 15:00 (3:00 PM),
 * whichever is later. On successful clock-out, it updates the shift data in the TimekeepingContext
 * and displays a success message.
 *
 * @component
 * @param {Object} props
 * @param {string} props.date - The date for which the clock-out is being recorded (YYYY-MM-DD format)
 * @param {boolean} [props.startVisible] When true, shows clock-out content immediately.
 * @param {Function} [props.setDayComplete] Called with true after a successful clock-out.
 *
 * @example
 * <ClockOut date="2023-06-15" />
 *
 * @returns {JSX.Element} The ClockOut component
 */
const ClockOut = ({ date, startVisible, setDayComplete }) => {
  const { user } = useContext(CoreContext);
  const { t } = useUiText();
  const {
    showClockOutContent,
    setShowClockOutContent,
    fetchWeeklyShifts,
    dailyShiftData,
    // Get message state from context
    message,
    setMessageDebounced,
  } = useContext(TimekeepingContext); // State management
  const currentShift = dailyShiftData?.find(
    (shift) => shift.stage === "Clocked In"
  );
  const [finishTime, setFinishTime] = useState("15:00"); // Default time out set to 15:00
  const [showBreaksModal, setShowBreaksModal] = useState(false); // Modal visibility
  const [isLoading, setIsLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Show clock out content if startVisible is true
  useEffect(() => {
    if (startVisible) {
      setShowClockOutContent(true);
    }
  }, [startVisible, setShowClockOutContent]);

  // Set default time to clock in time + 8 or current time if within -2 hour window
  useEffect(() => {
    if (currentShift && currentShift.startTime) {
      setFinishTime(
        getCurrentTimeInWindow(
          addHoursTo24TimeString(currentShift.startTime, 6),
          addHoursTo24TimeString(currentShift.startTime, 8),
          addHoursTo24TimeString(currentShift.startTime, 8)
        )
      );
    }
  }, [currentShift]);

  /**
   * Validate finish time vs clock-in, then open the breaks confirmation modal.
   *
   * @returns {Promise<void>}
   */
  const handleClockOut = async () => {
    // Get the clock out shift data, there should only ever be one clocked in shift per day

    if (!finishTime) {
      setMessageDebounced({ type: "error", text: t(UI_COPY.clockIn.clockOut.enterTime) });
      return;
    }

    if (finishTime < currentShift?.startTime) {
      setMessageDebounced({
        type: "error",
        text: t(UI_COPY.clockIn.clockOut.beforeClockIn),
      });
      return;
    }

    // Show the breaks modal instead of immediately clocking out
    setShowBreaksModal(true);
  };

  /**
   * Persist clock-out with the breaks answer, then refresh weekly shifts.
   *
   * @param {boolean} breaksCompleted Whether required breaks were completed.
   * @returns {Promise<void>}
   */
  const handleBreaksConfirmation = async (breaksCompleted) => {
    setShowBreaksModal(false);

    try {
      setIsLoading(true);
      const result = await clockOutUser({
        date,
        finishTime,
        userId: user?.id,
        breaksCompleted,
      });

      if (result.success) {
        setMessageDebounced({
          type: "success",
          text: t(UI_COPY.clockIn.clockOut.clockOutSuccessful),
        });
        setIsAnimating(true); // Hide clock out box after successful clock out
        setTimeout(() => {
          fetchWeeklyShifts(); // Fetch updated shift data
        }, 800); // Delay to allow animations to complete
        setDayComplete(true); // Set state to not allow clocking in again
      } else {
        setMessageDebounced({
          type: "error",
          text: result.message || t(UI_COPY.clockIn.clockOut.clockOutFailed),
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error clocking out:", error);
      setMessageDebounced({
        type: "error",
        text: t(UI_COPY.clockIn.clockOut.clockOutError),
      });
      setIsLoading(false);
    }
  };

  return (
    <div>
      {/* Clock Out Form */}
      <div
        className={`
        opacity-0 mt-2
        ${showClockOutContent ? "animate-expand-down" : ""} 
        bg-gray-100 ${isAnimating ? "py-0 px-6" : "p-2"} rounded-md 
        transition-all duration-1000 ease-in-out`}
      >
        <div
          className={`CLOCK-OUT w-full
          ${isAnimating ? "animate-shrink-hide" : showClockOutContent ? "opacity-100" : "opacity-0"}
          transition-all duration-1000 delay-500 origin-top`}
        >
          <h2 className="flex items-center text-lg text-black font-medium mb-4">
            <Clock className="text-gray-700 mr-2" />
            {t(UI_COPY.clockIn.clockOut.title)}
          </h2>{" "}
          {message && message.topic !== "schedule" && (
            <div
              className={`mb-4 p-3 ${BORDERS.radius.sm} ${STATUS_MESSAGE_COLORS[message.type] || STATUS_MESSAGE_COLORS.success}`}
            >
              {message.text}
            </div>
          )}{" "}
          {/* Time Out */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t(UI_COPY.clockIn.clockOut.timeOut)}
            </label>
            <input
              type="time"
              value={getCurrentTimeInWindow(
                finishTime,
                addHoursTo24TimeString(finishTime, -2),
                finishTime
              )}
              onChange={(e) => setFinishTime(e.target.value)}
              className="block w-full border border-gray-300 rounded-md py-2 px-3 text-base focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />{" "}
          </div>{" "}
          {/* Clock Out Button */}
          <Button
            onClick={() => {
              handleClockOut();
            }}
            variant={isLoading ? "disabled" : "secondary"}
            text={isLoading ? "" : t(UI_COPY.clockIn.clockOut.submit)}
            icon={isLoading ? "spinner" : null}
            className="py-3 px-6 mt-8 sm:mt-0 w-full"
          />
        </div>
      </div>

      {/* Breaks Confirmation Modal */}
      <ModalWrapper
        isOpen={showBreaksModal}
        onClose={() => setShowBreaksModal(false)}
        closeButton={false}
      >
        <div className="text-center p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            {t(UI_COPY.clockIn.clockOut.breakTitle)}
          </h2>
          <p className="text-gray-600 mb-8">
            {t(UI_COPY.clockIn.clockOut.breakPrompt)}
          </p>
          <div className="flex gap-4 justify-center">
            <Button
              onClick={() => handleBreaksConfirmation(true)}
              variant="primary"
              text={t(UI_COPY.clockIn.clockOut.breakYes)}
              className="px-6 py-2"
            />
            <Button
              onClick={() => handleBreaksConfirmation(false)}
              variant="secondary"
              text={t(UI_COPY.clockIn.clockOut.breakNo)}
              className="px-6 py-2"
            />
          </div>
        </div>
      </ModalWrapper>
    </div>
  );
};

export default ClockOut;
